import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Page, PageHeader, Section, Stack } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { aiPodBillingService, type FeatureMeta } from '../../../services/aiPodBillingService';
import { aiPodService, type AiPod } from '../../../services/aiPodService';
import { PlanModal } from './components/PlanModal';
import { PodPlansModal } from './components/PodPlansModal';
import { Badge } from '../../../components/ui/Badge';
import { SelectField } from '../../../components/ui/Field';
import { Overlay } from '../../../components/ui/Overlay';

const selectCls = 'h-9 rounded-xl border border-agentic-gray-300 bg-white px-3 text-sm text-agentic-text-primary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2';

const PlansPage: React.FC = () => {
  const [pods, setPods] = useState<AiPod[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activePod, setActivePod] = useState<AiPod | null>(null);

  const [podPickerOpen, setPodPickerOpen] = useState(false);
  const [selectedPodId, setSelectedPodId] = useState('');
  const [creatingForPod, setCreatingForPod] = useState<AiPod | null>(null);
  const [newPlanFeatures, setNewPlanFeatures] = useState<FeatureMeta[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiPodService.listAiPods({ page: 1, limit: 100 });
      setPods(res.aiPods ?? []);
    } catch {
      // apiClient already surfaces a toast for the failed request.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const managePlans = (pod: AiPod) => setActivePod(pod);

  const openPodPicker = () => {
    setSelectedPodId('');
    setPodPickerOpen(true);
  };

  const startCreatePlan = async () => {
    const pod = pods.find((p) => p.id === selectedPodId);
    if (!pod) return;
    setLoadingFeatures(true);
    try {
      const res = await aiPodBillingService.getPlanFeatures(pod.id);
      setNewPlanFeatures(res.features);
      setCreatingForPod(pod);
      setPodPickerOpen(false);
    } catch {
      // apiClient already surfaces a toast for the failed request.
    } finally {
      setLoadingFeatures(false);
    }
  };

  const handleCreatePlan = async (payload: any, rows: { featureId: string; interval: string; value: any }[]) => {
    if (!creatingForPod) return;
    const created = await aiPodBillingService.createPlan(creatingForPod.id, payload);
    await aiPodBillingService.replacePlanFeatures(creatingForPod.id, created.id, rows);
    toast.success('Plan created');
    setCreatingForPod(null);
    setSelectedPodId('');
  };

  const filtered = search.trim()
    ? pods.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.code?.toLowerCase().includes(search.toLowerCase())
      )
    : pods;

  return (
    <Page>
      <PageHeader
        title="Plans"
        subtitle="Select an AI Pod to manage its billing plans."
        aside={<Button size="sm" onClick={openPodPicker}>+ New Plan</Button>}
      />

      <Section>
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <Input
            placeholder="Search pods…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            className="w-64"
          />
          {filtered.length > 0 && (
            <span className="ml-auto text-xs text-agentic-text-secondary">
              {filtered.length} pod{filtered.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <Stack space="lg">
          {loading ? (
            <div className="py-16 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mb-4 text-6xl">📦</div>
              <h3 className="mb-2 text-xl font-semibold text-agentic-text-primary">No AI pods found</h3>
              <p className="text-agentic-text-secondary">No pods match your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((pod) => (
                <Card
                  key={pod.id}
                  hover
                  className="rounded-2xl"
                  title={
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 flex-1 truncate">{pod.name || pod.code}</span>
                    </div>
                  }
                  subtitle={`${pod.code} • ${pod.isActive ? 'Active' : 'Inactive'} • ${pod.agentTypes?.length || 0} agent type(s)`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light text-white">
                      <span className="text-lg">📦</span>
                    </div>
                    <div className="text-sm text-agentic-text-secondary">
                      {pod.shortDescription || pod.description || pod.category?.name || ''}
                    </div>
                  </div>
                  {pod.agentTypes?.length ? (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {[...pod.agentTypes]
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((ref, idx) => (
                          <Badge tone="primary-soft"
                            key={ref.id}
                            className="rounded-full px-2 py-0.5 text-xs"
                          >
                            {idx + 1}. {ref.agentType.name}
                          </Badge>
                        ))}
                    </div>
                  ) : null}
                  <div className="mt-4 text-end">
                    <Button tone="primary-gradient"
                      className="shrink-0 rounded-xl px-4 py-2 transition-all"
                      onClick={() => managePlans(pod)}
                    >
                      Manage Plans
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Stack>
      </Section>

      {podPickerOpen && (
        <Overlay tone="dim-40" className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-1">New Plan</h3>
            <p className="text-sm text-agentic-text-secondary mb-4">Which Pod is this plan for?</p>
            <SelectField
              className={`${selectCls} w-full`}
              value={selectedPodId}
              onChange={(e) => setSelectedPodId(e.target.value)}
            >
              <option value="">Select a Pod…</option>
              {pods.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </SelectField>
            <div className="mt-5 flex justify-end gap-2">
              <Button tone="custom"
                onClick={() => { setPodPickerOpen(false); setSelectedPodId(''); }}
                className="px-4 py-2 text-sm border border-agentic-gray-300 rounded-lg"
              >
                Cancel
              </Button>
              <Button size="sm" disabled={!selectedPodId || loadingFeatures} onClick={startCreatePlan}>
                {loadingFeatures ? 'Loading…' : 'Continue'}
              </Button>
            </div>
          </div>
        </Overlay>
      )}

      {creatingForPod && (
        <PlanModal
          plan={null}
          allFeatures={newPlanFeatures}
          onClose={() => { setCreatingForPod(null); setSelectedPodId(''); }}
          onSave={handleCreatePlan}
        />
      )}

      {activePod && (
        <PodPlansModal pod={activePod} onClose={() => setActivePod(null)} />
      )}
    </Page>
  );
};

export default PlansPage;
